import { useEffect, useRef } from 'react'
import { showVersionUpdateToast } from '@/components/shared/VersionUpdateToast'
import { env } from '@/config/env'

const VERSION_MANIFEST_URL = '/version.json'
const FALLBACK_POLL_INTERVAL_MS = 5 * 60 * 1000

interface VersionManifest {
  version: string
}

async function fetchDeployedVersion(signal: AbortSignal): Promise<string | null> {
  try {
    const response = await fetch(VERSION_MANIFEST_URL, { cache: 'no-store', signal })
    if (!response.ok) {
      return null
    }
    const manifest = (await response.json()) as VersionManifest
    return typeof manifest.version === 'string' ? manifest.version : null
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') {
      return null
    }
    console.warn('[VersionUpdate] Could not fetch deployed version', error)
    return null
  }
}

/**
 * Detects when a newer frontend build has been deployed and prompts the user to
 * reload before they hit a missing lazy chunk.
 *
 * The build id compiled into the running bundle (`__BUILD_ID__`) is compared against
 * the `version.json` served from the same origin. The check runs on mount, whenever
 * the tab regains focus/visibility, and on a slow fallback interval — never while the
 * tab is hidden, to avoid pointless background traffic. The toast is shown at most
 * once per session and only in production builds.
 */
export function useVersionUpdate(): void {
  const hasNotifiedRef = useRef(false)

  useEffect(() => {
    if (env.isDevelopment) {
      return
    }

    const runningVersion = __BUILD_ID__
    let controller = new AbortController()

    const checkForUpdate = async (): Promise<void> => {
      if (hasNotifiedRef.current || document.visibilityState === 'hidden') {
        return
      }

      controller.abort()
      controller = new AbortController()

      const deployedVersion = await fetchDeployedVersion(controller.signal)
      if (!deployedVersion || deployedVersion === runningVersion) {
        return
      }

      hasNotifiedRef.current = true
      showVersionUpdateToast()
    }

    const handleVisibilityChange = (): void => {
      if (document.visibilityState === 'visible') {
        void checkForUpdate()
      }
    }

    const handleFocus = (): void => {
      void checkForUpdate()
    }

    void checkForUpdate()
    const intervalId = window.setInterval(() => {
      void checkForUpdate()
    }, FALLBACK_POLL_INTERVAL_MS)
    document.addEventListener('visibilitychange', handleVisibilityChange)
    window.addEventListener('focus', handleFocus)

    return () => {
      controller.abort()
      window.clearInterval(intervalId)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      window.removeEventListener('focus', handleFocus)
    }
  }, [])
}
